import { useContext } from 'react';
import { optionalProp } from '../core';
import { useConfig } from '../Config/ConfigProvider';
import { ViewPortSizes } from '../Breakpoint/BreakpointProvider';
import { replaceCSToCDN, getContentStackURL, getQuality, getNextImageSizes, LoaderType } from './NextgenImage';
import FallbackNextgenImage from './FallbackNextgenImage';
import { NextgenImageContext, NextgenImageProvider } from './NextgenImageProvider';
import { type NextgenImageProps, type NextgenImageSrcObject, ImageSourceType } from './types/propsTypes';

const NextgenImageV2 = ({
  fill = true,
  quality = 101,
  // TODO: Make CONTENTSTACK value default after PODs make their changes
  loaderType,
  isRelativeURL = false,
  fallbackQuality = 100,
  format = 'avif',
  src,
  priority,
  forceCdnUrlConversion = false,
  imageWidths,
  isSensitiveField = false,
  ...restProps
}: NextgenImageProps) => {
  const { element: NextJsImage, useCDNForCS, getImageProps } = useContext(NextgenImageContext);
  const configs = useConfig();
  const csImageUrl = configs.NEXT_PUBLIC_CONTENT_STACK_IMAGES_URL as string;
  const csCdnImageUrl = configs.NEXT_PUBLIC_CONTENT_STACK_CDN_URL as string;
  const CDNPostFix = configs.NEXT_PUBLIC_CONTENT_STACK_CDN_POSTFIX as string;
  const CDN_URL = `${configs.DEFAULT_HOST_URL}${CDNPostFix}`;
  const allowMasking = configs.NEXT_PUBLIC_ALLOW_MASKING_CHANGE;
  const configuredImageQuality = configs?.NEXT_PUBLIC_IMAGE_QUALITY as number;

  const isMaskingAllowed = (allowMasking: unknown, isSensitiveField: boolean) =>
    allowMasking ? isSensitiveField : undefined;

  const isImageFillAllowed = (fill: boolean, noFilledImage?: number | string) => (noFilledImage ? false : fill);

  const calculateImageQuality = <T extends number | string>(initialQuality: T): T | number => {
    if (initialQuality === 101) {
      return Number.isInteger(configuredImageQuality) ? configuredImageQuality : 100;
    }
    return initialQuality;
  };

  /**
   *  Quality resolution logic:
   *   - If prop value is between 0–100 → use it directly.
   *   - If prop value is 101 (default placeholder) → use config value if available, otherwise fallback to 100.
   *  Note: 101 indicates "not set" and helps differentiate from valid 0–100 quality values.
   **/
  const imgQuality = calculateImageQuality(quality);

  const hasMultipleSources = typeof src === 'object' && (src.mobile || src.tablet || src.desktop);
  /**
   * To reduce the dom size and to use the only single image instead of picture tag.
   */
  const isAllSrcDifferent =
    hasMultipleSources && (src.mobile !== src.tablet || src.tablet !== src.desktop || src.mobile !== src.desktop);

  const getUpdatedSrc = (originalSrc?: string) =>
    replaceCSToCDN(originalSrc as string, useCDNForCS, csImageUrl, CDN_URL, csCdnImageUrl, forceCdnUrlConversion);
  // Get updated URLs for all sources
  let updatedMobileSRC: string;
  let updatedTabletSRC: string;
  let updatedDesktopSRC: string;
  if (hasMultipleSources && isAllSrcDifferent) {
    const srcObj = src as NextgenImageSrcObject;
    updatedMobileSRC = getUpdatedSrc(srcObj.mobile);
    updatedTabletSRC = getUpdatedSrc(srcObj.tablet);
    updatedDesktopSRC = getUpdatedSrc(srcObj.desktop);
  } else if (hasMultipleSources && !isAllSrcDifferent) {
    const singleSrc = src.mobile;
    updatedMobileSRC = getUpdatedSrc(singleSrc);
    updatedTabletSRC = updatedMobileSRC;
    updatedDesktopSRC = updatedMobileSRC;
  } else {
    // Single source mode
    const singleSrc = typeof src === 'string' ? src : '';
    updatedMobileSRC = getUpdatedSrc(singleSrc);
    updatedTabletSRC = updatedMobileSRC;
    updatedDesktopSRC = updatedMobileSRC;
  }

  const customLoader = ({ src: loaderSrc, width }: { src: string; width: number }) => {
    if (loaderType === LoaderType.CONTENTSTACK) {
      return getContentStackURL(
        loaderSrc,
        useCDNForCS,
        CDN_URL,
        csImageUrl,
        width,
        format,
        imgQuality,
        fallbackQuality,
        isRelativeURL,
        csCdnImageUrl,
        forceCdnUrlConversion,
      );
    } else if (loaderType === LoaderType.WEBFONTS && isRelativeURL) {
      return `${configs.NEXT_ASSETS_BASE_PATH}${loaderSrc}`;
    }

    return loaderSrc;
  };
  const noFilledImage = restProps.height && restProps.width;
  const generateImageProps = (imageSrc: string) => {
    if (!NextJsImage) {
      return null;
    }

    return (
      getImageProps &&
      getImageProps({
        src: imageSrc,
        loader: customLoader,
        ...getQuality(loaderType, imgQuality),
        fill: noFilledImage ? false : fill,
        ...restProps,
        sizes: restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any)
    );
  };

  const renderNextImage = NextJsImage && (hasMultipleSources ? updatedMobileSRC : updatedDesktopSRC);

  if (!renderNextImage) {
    return (
      <FallbackNextgenImage
        src={updatedDesktopSRC}
        {...optionalProp('isSensitiveField', isMaskingAllowed(allowMasking, isSensitiveField))}
        {...restProps}
      />
    ); //Expecting single src for fallback
  }
  // Multi-source rendering
  if (hasMultipleSources && isAllSrcDifferent) {
    const tabletImageProps = generateImageProps(updatedTabletSRC);
    const desktopImageProps = generateImageProps(updatedDesktopSRC);
    return (
      <picture>
        {/* Desktop source */}
        <source
          media={`(min-width: ${ViewPortSizes.LARGE}px)`}
          srcSet={desktopImageProps?.props.srcSet}
          type={`image/${format}`}
          sizes={restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill)}
        />
        {/* Tablet source */}
        <source
          media={`(min-width: ${ViewPortSizes.MEDIUM}px)`}
          srcSet={tabletImageProps?.props.srcSet}
          type={`image/${format}`}
          sizes={restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill)}
        />
        {/* Mobile image (fallback) */}
        <NextJsImage
          src={updatedMobileSRC}
          loader={customLoader}
          {...getQuality(loaderType, imgQuality)}
          fill={isImageFillAllowed(fill, noFilledImage)}
          priority={priority}
          {...restProps}
          sizes={restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill)}
          data-dtrum-mask={isMaskingAllowed(allowMasking, isSensitiveField)}
        />
      </picture>
    );
  }
  // Single source rendering (existing behavior)
  return (
    <NextJsImage
      src={updatedDesktopSRC}
      loader={customLoader}
      {...getQuality(loaderType, imgQuality)}
      fill={isImageFillAllowed(fill, noFilledImage)}
      priority={priority}
      {...restProps}
      sizes={restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill)}
      data-dtrum-mask={isMaskingAllowed(allowMasking, isSensitiveField)}
    />
  );
};

export { replaceCSToCDN, getContentStackURL, NextgenImageV2, NextgenImageProvider, LoaderType, ImageSourceType };
export type { NextgenImageProps, NextgenImageSrcObject };
